import React, { useMemo, useState } from 'react';
import { get } from 'lodash';

import { objectToString } from 'helper';
import Button from 'partial/components/Button';
import ThumbIcon from 'partial/components/ThumbIcon';
import { uploadPhoto } from 'partial/form/FormPhotoDnd';
import { useCreateModal } from 'partial/components/Modal';
import GenericDetails from 'partial/components/GenericDetails';
import { useActiveAccount, useChangePassword } from 'modules/auth/hooks';
import ChangePasswordModal from 'partial/components/modals/ChangePasswordModal';

import { useGetProfile, useUploadOrgPhoto } from '../hooks';

function ProfileCard() {
  const [data] = useGetProfile();
  const [newUrl, setNewUrl] = useState('');
  const [activeAccount] = useActiveAccount();
  const [upload, isUploading] = useUploadOrgPhoto();
  const logo = data?.logo ? data?.logo[0]?.url || data?.logo : '';
  const [changePassword, isChangePasswordLoading] = useChangePassword();

  const modal = useCreateModal('CHANGE_PASSWORD', { isChangePasswordLoading });
  const handleChangePassword = () => {
    modal({
      // eslint-disable-next-line
      content: (onClose) => ({ isChangePasswordLoading: isLoadingProp }) => (
        <ChangePasswordModal
          submit={changePassword}
          isLoading={isLoadingProp}
          onClose={onClose}
        />
      ),
    });
  };
  const handleUploadPhoto = async () => {
    const url = await uploadPhoto({
      crop: '1:1',
      imagesOnly: true,
      inputAcceptTypes: 'image/png, image/jpeg',
    });
    if (url) {
      upload({ logo: objectToString(url) });
      setNewUrl(url);
    }
  };

  const profileFormat = useMemo(() => {
    const col = [
      {
        label: 'Organization Name',
        key: 'company_name',
      },
      {
        label: 'First Name',
        key: 'first_name',
      },
      {
        label: 'Last Name',
        key: 'last_name',
      },
      {
        label: 'Email Address',
        key: 'email_address',
      },
      {
        label: 'Status',
        key: (row) => (
          <p
            className={`capitalize
             ${
               get(row, 'status') === 'ACTIVE'
                 ? 'text-secondary-500'
                 : 'text-danger-500'
             }
            `}
          >
            {get(row, 'status')?.toLowerCase()}
          </p>
        ),
      },
    ];
    if (activeAccount?.role === 'biller') {
      col.splice(1, 0, {
        label: 'Biller Name',
        key: 'biller_name',
        span: 2,
      });
    }
    return col;
  }, [activeAccount]);

  return (
    <div className="flex-1 bg-white rounded-xl p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">
          {activeAccount?.role === 'organization'
            ? data?.company_name
            : data?.biller_name}
        </h2>
        <div>
          <Button type="button" outlinePrimary onClick={handleChangePassword}>
            Change Password
          </Button>
        </div>
      </div>
      <div className="flex justify-between mt-4 pt-4 pb-4 border-t border-b">
        <div className="relative">
          <ThumbIcon
            url={newUrl || logo}
            alt={data?.company_name}
            className="h-40 w-40 rounded-xl"
          />
          {isUploading && (
            <div className="absolute inset-0 px-2 bg-white bg-opacity-70">
              <div className="text-gray-600 text-center mt-16">
                Uploading...
              </div>
              <div className="loaderBar">
                <div className="loaderBarInner" />
              </div>
            </div>
          )}
        </div>
        <div className="w-full ml-8">
          <GenericDetails data={data} format={profileFormat} numberOfCols={3} />
        </div>
      </div>
      {activeAccount?.role === 'organization' && (
        <div className="mt-4">
          <Button
            primary
            type="button"
            onClick={handleUploadPhoto}
            isLoading={isUploading}
          >
            Upload Organization Photo
          </Button>
        </div>
      )}
    </div>
  );
}

export default ProfileCard;
